import React, { useState, useEffect } from 'react';
import { DropdownMenu } from '../DropdownMenu';
import TextInput from '../TextInput';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';

enum Metal {
    Ta = "Ta",
    Sn = "Sn",
    Au = "Au",
    W = "W"
}

const metalsDict: { [key: string]: string } = {
    Ta: "Tantalum (Ta)",
    Sn: "Tin (Sn)",
    Au: "Gold (Au)",
    W: "Tungsten (W)"
};

interface ConflictMaterialMetalModel {
    name: string;
    value: string;
    comment: string;
}

interface ConflictMaterialsMultiMaterialInputProps {
    localizer: IStringLocalizer;
    idPrefix?: string;
    isDisabled?: boolean;
    value: string; // Serialized list of ConflictMaterialMetalModel
    onValueChanged: (newValue: string) => void;
}

const ConflictMaterialsMultiMaterialInput: React.FC<ConflictMaterialsMultiMaterialInputProps> = ({
    localizer,
    idPrefix = "",
    isDisabled = false,
    value,
    onValueChanged
}) => {
    const [deserializedValue, setDeserializedValue] = useState<ConflictMaterialMetalModel[]>([]);

    useEffect(() => {
        try {
            const parsedValue = JSON.parse(value) as ConflictMaterialMetalModel[];
            setDeserializedValue(parsedValue);
        } catch (e) {
            console.error("Failed to parse value", e);
            setDeserializedValue([]);
        }
    }, [value]);

    const updateSerializedValue = (updatedModels: ConflictMaterialMetalModel[]) => {
        setDeserializedValue(updatedModels);
        onValueChanged(JSON.stringify(updatedModels));
    };

    const getDropdownMetalValue = (metal: string): string => {
        const metalEntry = deserializedValue.find((entry) => entry.name === metal);
        return metalEntry?.value || "";
    };

    const setDropdownMetalValue = (metal: string, newValue: string): void => {
        const updatedValue = deserializedValue.map((entry) =>
            entry.name === metal ? { ...entry, value: newValue } : entry
        );
        updateSerializedValue(updatedValue);
    };

    const getDropdownCommentsAgainstMetal = (metal: string): string => {
        const metalEntry = deserializedValue.find((entry) => entry.name === metal);
        return metalEntry?.comment || "";
    };

    const setDropdownCommentsAgainstMetal = (metal: string, newValue: string): void => {
        const updatedValue = deserializedValue.map((entry) =>
            entry.name === metal ? { ...entry, comment: newValue } : entry
        );
        updateSerializedValue(updatedValue);
    };

    const optionsYesNo = [localizer.localizeString("Choose one"), localizer.localizeString("Yes"),localizer.localizeString("No")];

    return (
        <div className="question">
            <div className="question-response m-l-15">
                {Object.keys(Metal).map((key) => (
                    <div key={key} className="row">
                        {/* Dropdown for each metal */}
                        <div className="form-group col-xs-3">
                            <label>{metalsDict[key]}</label>
                            <DropdownMenu
                                options={optionsYesNo}
                                onOptionSelected={(newValue) => setDropdownMetalValue(key, newValue)}
                                getFriendlyNameForOptions={(o) => o}
                                value={getDropdownMetalValue(key)}
                                getUniqueValueForOption={(o) => o}
                                id={`${idPrefix}-${key}`}
                                isDisabled={isDisabled}
                            />
                        </div>

                        {/* TextInput for comments */}
                        <div className="form-group col-xs-9">
                            <label>{localizer.localizeString("Comment")}</label>
                            <TextInput
                                value={getDropdownCommentsAgainstMetal(key)}
                                onValueChange={(newValue) => setDropdownCommentsAgainstMetal(key, newValue)}
                                idPrefix={`${idPrefix}-comment-${key}`}
                                disabled={isDisabled}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ConflictMaterialsMultiMaterialInput;
